import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MessageCircle, Search, Loader2, User } from 'lucide-react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import DoubtChatView from '../components/DoubtChatView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DoubtEngineTeacher() {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null); // {chat, messages}
  const [openingChat, setOpeningChat] = useState(false);

  const fetchChats = async () => {
    try {
      const res = await api.getDoubtChats();
      setChats(res.data || []);
    } catch (e) {
      toast.error('Failed to load chats');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchChats();
    const t = setInterval(fetchChats, 15000); // gentle poll to refresh unread counts / new chats
    return () => clearInterval(t);
  }, []);

  const openChat = async (chat) => {
    setOpeningChat(true);
    try {
      const res = await api.getDoubtChatMessages(chat.id);
      setSelected({ chat, messages: res.data.messages || [] });
      // Locally clear unread badge
      setChats((cs) => cs.map((c) => (c.id === chat.id ? { ...c, teacher_unread_count: 0 } : c)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot open chat');
    } finally { setOpeningChat(false); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return chats;
    return chats.filter((c) => (
      (c.student_name || '').toLowerCase().includes(s) ||
      (c.student_phone || '').includes(s) ||
      (c.course_name || '').toLowerCase().includes(s)
    ));
  }, [chats, q]);

  const wsUrl = (chatId) => {
    const token = authService.getToken();
    if (!token || !chatId) return null;
    const base = BACKEND_URL.replace(/^http/, 'ws');
    return `${base}/api/doubt-engine/ws/${chatId}?token=${encodeURIComponent(token)}&role=teacher`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  }

  return (
    <div data-testid="doubt-engine-teacher" className="max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">My Doubt Inbox</h1>
        <p className="text-sm text-slate-600 mt-1">Student doubts assigned to you across all batches.</p>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-3 bg-white rounded-2xl border border-slate-200 overflow-hidden"
        style={{ height: 'calc(100dvh - 180px)', minHeight: '520px' }}
      >
        {/* Chat list */}
        <div className="border-r border-slate-200 flex flex-col min-h-0">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, phone, batch…"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="doubt-teacher-search"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-slate-100">
            {filtered.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                <MessageCircle size={36} className="mx-auto mb-2 text-slate-300" />
                No chats yet
              </div>
            )}
            {filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => openChat(c)}
                data-testid={`doubt-teacher-chat-${c.id}`}
                className={`w-full text-left p-3 hover:bg-slate-50 transition ${selected?.chat?.id === c.id ? 'bg-emerald-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{c.student_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.course_name}</div>
                    <div className="text-xs text-slate-500 truncate mt-1">{c.last_message_preview || 'No messages yet'}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-[10px] text-slate-400 whitespace-nowrap">
                      {c.last_message_at ? new Date(c.last_message_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                    </div>
                    {c.teacher_unread_count > 0 && (
                      <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                        {c.teacher_unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat view */}
        <div className="flex flex-col min-h-0">
          {openingChat && !selected && (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <Loader2 className="animate-spin mr-2" /> Opening chat…
            </div>
          )}
          {!selected && !openingChat && (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500">
              <User size={64} className="opacity-30" />
              <p className="mt-3 text-sm">Select a student chat to reply</p>
            </div>
          )}
          {selected && (
            <div className="flex-1 flex flex-col min-h-0">
              {selected.chat.status === 'closed' && (
                <div className="px-3 py-2 bg-emerald-900/40 border-b border-emerald-700/50 text-emerald-200 text-[11px] flex items-center gap-2" data-testid="doubt-teacher-closed-banner">
                  <span className="font-semibold">Closed</span>
                  <span>· This chat was closed by the student{selected.chat.closed_at ? ` on ${new Date(selected.chat.closed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}.</span>
                </div>
              )}
              <DoubtChatView
                chatId={selected.chat.id}
                wsUrl={wsUrl(selected.chat.id)}
                identity="teacher"
                initialMessages={selected.messages}
                header={{
                  title: selected.chat.student_name,
                  subtitle: `${selected.chat.student_phone} · ${selected.chat.course_name}`,
                }}
                disabled={selected.chat.status === 'closed'}
                onClosed={(updated) => {
                  setSelected((s) => s ? { ...s, chat: { ...s.chat, ...(updated || {}), status: 'closed' } } : s);
                }}
                onSendText={async (content) => (await api.sendDoubtStaffText(selected.chat.id, content)).data}
                onSendMedia={async (file, mt) => (await api.sendDoubtStaffMedia(selected.chat.id, file, mt)).data}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
